import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Snackbar,
  CircularProgress,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { ContentCopy as ContentCopyIcon } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import api from "../../../utils/api";
import { useDashboardTheme } from "../context/DashboardThemeContext";

interface WebhookConfigDialogProps {
  open: boolean;
  onClose: () => void;
}

const WebhookConfigDialog: React.FC<WebhookConfigDialogProps> = ({
  open,
  onClose,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isDark = useDashboardTheme();

  const [webhookUrl, setWebhookUrl] = useState("");
  const [verifyToken, setVerifyToken] = useState("");
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
  });

  useEffect(() => {
    if (open) {
      setLoading(true);
      api
        .get("/api/webhook-config")
        .then((res) => {
          setWebhookUrl(res.data.webhookUrl || "");
          setVerifyToken(res.data.verifyToken || "");
        })
        .catch(() => {
          setWebhookUrl("");
          setVerifyToken("");
        })
        .finally(() => setLoading(false));
    }
  }, [open]);

  const handleCopy = (value: string) => {
    navigator.clipboard.writeText(value);
    setSnackbar({
      open: true,
      message: t("webhook_config.copied"),
    });
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="sm"
        fullWidth
        fullScreen={isMobile}
        PaperProps={{
          sx: {
            maxHeight: "80vh",
            height: isMobile ? "100vh" : "auto",
            bgcolor: isDark ? "rgba(20, 20, 20, 0.98)" : undefined,
            border: isDark ? "1px solid rgba(255,255,255,0.08)" : undefined,
          },
        }}
      >
        <DialogTitle
          sx={{
            background: isDark
              ? "linear-gradient(90deg, #2d1b69 0%, #1a3a4a 100%)"
              : "linear-gradient(90deg, #93F090 0%, #60BFB7 100%)",
            color: "#fff",
            py: 2,
            px: 3,
          }}
        >
          {t("webhook_config.title")}
        </DialogTitle>

        <DialogContent
          sx={{
            overflowY: "auto",
            pt: 3,
            "&::-webkit-scrollbar": { width: "8px" },
            "&::-webkit-scrollbar-track": { background: "#f1f1f1" },
            "&::-webkit-scrollbar-thumb": {
              background: "#888",
              borderRadius: "4px",
            },
            "&::-webkit-scrollbar-thumb:hover": { background: "#555" },
          }}
        >
          {loading ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                py: 4,
              }}
            >
              <CircularProgress />
            </Box>
          ) : (
            <>
              <Typography
                variant="body2"
                sx={{ mb: 3, mt: 1, color: isDark ? "#a0a0a0" : undefined }}
              >
                {t("webhook_config.description")}
              </Typography>

              {/* Webhook Callback URL */}
              <Box sx={{ mb: 2 }}>
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 500,
                    mb: 0.5,
                    color: isDark ? "#e0e0e0" : undefined,
                  }}
                >
                  {t("webhook_config.callback_url")}
                </Typography>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    bgcolor: isDark ? "rgba(255,255,255,0.05)" : "#f5f5f5",
                    border: isDark
                      ? "1px solid rgba(255,255,255,0.08)"
                      : undefined,
                    p: 1,
                    borderRadius: 1,
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      flex: 1,
                      fontFamily: "monospace",
                      fontSize: "0.85rem",
                      wordBreak: "break-all",
                      color: isDark ? "#c0c0c0" : undefined,
                    }}
                  >
                    {webhookUrl}
                  </Typography>
                  <IconButton
                    size="small"
                    onClick={() => handleCopy(webhookUrl)}
                    sx={{ color: isDark ? "#a0a0a0" : undefined }}
                  >
                    <ContentCopyIcon fontSize="small" />
                  </IconButton>
                </Box>
              </Box>

              {/* Verify Token */}
              <Box sx={{ mb: 3 }}>
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 500,
                    mb: 0.5,
                    color: isDark ? "#e0e0e0" : undefined,
                  }}
                >
                  {t("webhook_config.verify_token")}
                </Typography>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    bgcolor: isDark ? "rgba(255,255,255,0.05)" : "#f5f5f5",
                    border: isDark
                      ? "1px solid rgba(255,255,255,0.08)"
                      : undefined,
                    p: 1,
                    borderRadius: 1,
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      flex: 1,
                      fontFamily: "monospace",
                      fontSize: "0.85rem",
                      wordBreak: "break-all",
                      color: isDark ? "#c0c0c0" : undefined,
                    }}
                  >
                    {verifyToken}
                  </Typography>
                  <IconButton
                    size="small"
                    onClick={() => handleCopy(verifyToken)}
                    sx={{ color: isDark ? "#a0a0a0" : undefined }}
                  >
                    <ContentCopyIcon fontSize="small" />
                  </IconButton>
                </Box>
              </Box>

              {/* Setup Instructions */}
              <Alert severity="info" sx={{ mb: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 500, mb: 1 }}>
                  {t("webhook_config.instructions_title")}
                </Typography>
                <Box component="ol" sx={{ pl: 2, m: 0, "& li": { mb: 0.5 } }}>
                  <li>
                    <Typography variant="body2">
                      {t("webhook_config.step1")}
                    </Typography>
                  </li>
                  <li>
                    <Typography variant="body2">
                      {t("webhook_config.step2")}
                    </Typography>
                  </li>
                  <li>
                    <Typography variant="body2">
                      {t("webhook_config.step3")}
                    </Typography>
                  </li>
                  <li>
                    <Typography variant="body2">
                      {t("webhook_config.step4")}
                    </Typography>
                  </li>
                </Box>
              </Alert>
            </>
          )}
        </DialogContent>

        <DialogActions
          sx={{
            px: 3,
            py: 2,
            borderTop: isDark
              ? "1px solid rgba(255,255,255,0.08)"
              : "1px solid #e0e0e0",
          }}
        >
          <Button
            variant="contained"
            onClick={onClose}
            sx={{
              textTransform: "none",
              background: "linear-gradient(135deg, #8a4fff 0%, #6b73ff 100%)",
              px: 3,
              "&:hover": {
                background: "linear-gradient(135deg, #7a3fef 0%, #5b63ef 100%)",
              },
            }}
          >
            {t("webhook_config.done")}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={2000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        message={snackbar.message}
      />
    </>
  );
};

export default WebhookConfigDialog;
